import { ExportKind } from '../entity/api/export';
import { type Product } from '../entity/api/product';
import { type ProductSection } from '../entity/api/product-section';
import {
  type CreateProductReq,
  type CreateProductRes,
  type DelProductBulkRes,
  type DelProductRes,
  type ExportContentRes,
  type GetProductRes,
  type GetProductsByBrandIdRes,
  type ListRes,
  type ProductSortAndFilter,
  type RetryGenerationRes,
  type SaveProductBulkRes,
  type SaveProductRes,
  createProductReqSchema,
  delProductBulkReqSchema,
  delProductReqSchema,
  exportProductReqSchema,
  getProductReqSchema,
  retryGenerationReqSchema,
  saveProductBulkReqSchema,
  saveProductReqSchema,
} from '../entity/api/routes/product';
import { dasherize } from '../shared/string';
import { removeElement } from '../shared/tiptap/dom-utilities';
import { apiRequest, setURL } from './request';

function buildPaginationQueryString(params?: ProductSortAndFilter): string {
  if (params == null) {
    return '';
  }

  const queryParams = new URLSearchParams();

  if (params.offset != null) {
    queryParams.append('offset', params.offset.toString());
  }

  if (params.limit != null) {
    queryParams.append('limit', params.limit.toString());
  }

  if (params.search != null && params.search.length > 0) {
    queryParams.append('search', params.search);
  }

  if (params.createdBy != null && params.createdBy.length > 0) {
    for (const userId of params.createdBy) {
      queryParams.append('createdBy[]', userId);
    }
  }

  if (params.sortBy != null) {
    queryParams.append('sortBy', params.sortBy);
  }

  if (params.sortByDir != null) {
    queryParams.append('sortByDir', params.sortByDir);
  }

  const queryString = queryParams.toString();
  return queryString.length > 0 ? `?${queryString}` : '';
}

export async function getProductsList() {
  return apiRequest<ListRes>('/product/list', 'GET', true);
}

export function getProductsByBrandId(brandId: string, queryParams?: ProductSortAndFilter) {
  const queryString = buildPaginationQueryString(queryParams);
  const url = `/product/get-by-brand/${brandId}${queryString}`;
  return apiRequest<GetProductsByBrandIdRes>(url, 'GET', true);
}

export async function getProductByid(id: string) {
  const req = getProductReqSchema.parse({
    params: { id },
    body: {},
  });
  const url = setURL(`/product/${id}`, req.query);
  return apiRequest<GetProductRes>(url, 'GET', true, req.body);
}

export async function createProduct(data: CreateProductReq['body']) {
  const req = createProductReqSchema.parse({
    params: null,
    body: data,
  });
  const url = setURL('/product', req.query);
  return apiRequest<CreateProductRes>(url, 'POST', true, req.body);
}

export async function updateProduct(product: Partial<Product>, sections?: ProductSection[]) {
  const { _id: id } = product;
  const req = saveProductReqSchema.parse({
    params: { id },
    body: { product, sections },
  });
  const url = setURL(`/product/${id}`, req.query);
  return apiRequest<SaveProductRes>(url, 'PATCH', true, req.body);
}

export async function retryGeneration(id: string) {
  const req = retryGenerationReqSchema.parse({
    params: { id },
    body: {},
  });
  const url = setURL(`/product/${id}/retry`, req.query);
  return apiRequest<RetryGenerationRes>(url, 'POST', true, req.body);
}

export async function updateProductBulk(products: Partial<Product>[], sections?: ProductSection[]) {
  const req = saveProductBulkReqSchema.parse({
    params: null,
    body: { products, sections: sections ?? [] },
  });
  const url = setURL('/product/save-bulk', req.query);
  return apiRequest<SaveProductBulkRes>(url, 'PATCH', true, req.body);
}

export async function deleteProduct(id: string) {
  const req = delProductReqSchema.parse({
    params: { id },
    body: null,
  });
  const url = setURL(`/product/${id}`, req.query);
  return apiRequest<DelProductRes>(url, 'DELETE', true, req.body);
}

export async function deleteProductBulk(ids: string[]) {
  const req = delProductBulkReqSchema.parse({
    params: null,
    body: { ids },
  });
  const url = setURL('/product/delete-bulk', req.query);
  return apiRequest<DelProductBulkRes>(url, 'POST', true, req.body);
}

export async function exportProductToCsv(ids: string[], productName?: string) {
  const req = exportProductReqSchema.parse({
    params: null,
    body: {
      kind: ExportKind.CSV,
      ids: ids,
    },
  });
  const url = setURL(`/product/export`, req.query);
  const result = await apiRequest<ExportContentRes>(url, 'POST', true, req.body);
  const fileName = dasherize(productName ?? 'products');
  const blob = new Blob([result.data], { type: 'text/csv' });
  const clickUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = clickUrl;
  link.setAttribute('download', `${fileName}.csv`);
  document.body.appendChild(link);
  link.click();
  removeElement(document.body, link);
  return true;
}
